import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, Plus, Pencil, Trash2, Save, Mail, ThumbsUp, ThumbsDown, Reply, Image as ImageIcon, Upload, Eye } from "lucide-react";

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "-");

export const buildGreeting = (contact, salutation) => {
  const cp = (contact || "").trim();
  if (!cp) return "Merhabalar";
  if (salutation === "Bey" || salutation === "Hanım") return `${cp.split(" ")[0].toLocaleUpperCase("tr-TR")} ${salutation}, Merhabalar`;
  return `Sayın ${cp}, Merhabalar`;
};

const OUTCOMES = [
  ["", "—", "bg-neutral-100 text-neutral-500"],
  ["replied", "Yanıt Geldi", "bg-[#0020C2] text-white"],
  ["positive", "Olumlu", "bg-[#16A34A] text-white"],
  ["negative", "Olumsuz", "bg-[#DC2626] text-white"],
];
export const outcomeMeta = (v) => OUTCOMES.find(([k]) => k === v) || OUTCOMES[0];

// ── Tek firmaya mail gönderme penceresi ──
export const SendMailModal = ({ firm, onClose, onSent }) => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [salutation, setSalutation] = useState("Sayın");
  const [extraNote, setExtraNote] = useState("");
  const [sending, setSending] = useState(false);
  const [signature, setSignature] = useState(null);
  const [withSignature, setWithSignature] = useState(true);

  useEffect(() => {
    api.get("/email-templates")
      .then(({ data }) => { setTemplates(data); if (data[0]) setTemplateId(data[0].id); })
      .catch((e) => toast.error(errMsg(e)));
    api.get("/mail-signature").then(({ data }) => setSignature(data)).catch(() => {});
  }, []);

  const tpl = templates.find(t => t.id === templateId);
  const greeting = buildGreeting(firm.contact_person, salutation);
  const preview = tpl
    ? tpl.body.replaceAll("{hitap}", greeting).replaceAll("{firma}", firm.name).replaceAll("{yetkili}", firm.contact_person || "")
      + (extraNote.trim() ? `\n\n${extraNote.trim()}` : "")
    : "";

  const send = async () => {
    if (!templateId) { toast.error("Taslak seçin"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/tender-tracking/${firm.id}/send-email`, {
        template_id: templateId, salutation, extra_note: extraNote,
        include_signature: withSignature && !!(signature?.has_image || signature?.disclaimer_text),
      });
      toast.success(`Mail gönderildi → ${firm.email} (${fmtDT(data.log.sent_at)})`, { duration: 7000 });
      onSent?.();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[600] flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl mt-10 border border-[#E5E5E5]" onClick={(e) => e.stopPropagation()} data-testid="send-mail-modal">
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Mail Gönder</div>
            <div className="font-bold font-heading">{firm.name} <span className="text-sm font-normal text-[#0020C2]">→ {firm.email}</span></div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="send-mail-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Taslak</label>
              <select data-testid="send-mail-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Hitap {firm.contact_person ? `(${firm.contact_person})` : "(yetkili yok)"}</label>
              <div className="mt-1 flex gap-1">
                {["Bey", "Hanım", "Sayın"].map(s => (
                  <button key={s} data-testid={`send-mail-sal-${s}`} onClick={() => setSalutation(s)}
                    className={`flex-1 px-2 py-2 text-xs font-bold border ${salutation === s ? "bg-[#0020C2] text-white border-[#0020C2]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>{s}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="text-xs bg-[#F5F8FF] border border-[#0020C2]/30 px-3 py-2">
            Hitap önizleme: <b data-testid="send-mail-greeting">{greeting}</b>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Konu</label>
            <div className="mt-1 px-3 py-2 bg-[#FAFAFA] border border-[#E5E5E5] text-sm">{tpl ? tpl.subject.replaceAll("{firma}", firm.name) : "-"}</div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">İçerik Önizleme (taslaktan)</label>
            <div data-testid="send-mail-preview" className="mt-1 px-3 py-2 bg-[#FAFAFA] border border-[#E5E5E5] text-sm whitespace-pre-wrap max-h-56 overflow-y-auto">
              {preview || "-"}
              {withSignature && signature && (signature.has_image || signature.disclaimer_text) && (
                <div className="mt-4 pt-3 border-t border-[#E5E5E5]" data-testid="send-mail-signature-preview">
                  {signature.has_image && <img src={signature.image_data_uri} alt="Mail imzası" className="max-w-[360px] w-full h-auto" />}
                  {signature.disclaimer_text && <div className="mt-2 text-[10px] text-neutral-400 leading-relaxed whitespace-pre-wrap">{signature.disclaimer_text}</div>}
                </div>
              )}
            </div>
          </div>
          {signature && (signature.has_image || signature.disclaimer_text) && (
            <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none">
              <input type="checkbox" checked={withSignature} onChange={(e) => setWithSignature(e.target.checked)}
                className="w-4 h-4 accent-[#0020C2]" data-testid="send-mail-signature-toggle" />
              Mail imzası ekle (görsel + yasal uyarı) <span className="text-neutral-400 font-normal">— imzayı "Mail Taslakları" ekranından düzenleyebilirsiniz</span>
            </label>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ek Not (bu maile özel, opsiyonel)</label>
            <textarea data-testid="send-mail-note" value={extraNote} onChange={(e) => setExtraNote(e.target.value)} rows={2} maxLength={2000}
              placeholder="örn: Gebze bölgesinde mevcut güzergahlarımız vardır."
              className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-neutral-400">Taslağın kendisini düzenlemek için "Mail Taslakları" ekranını kullanın. Yanıtlar oguzhan@keskintur.com adresine düşer.</span>
            <button data-testid="send-mail-submit" onClick={send} disabled={sending || !templateId}
              className="flex items-center gap-2 px-5 py-2.5 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {sending ? "Gönderiliyor..." : "Gönder"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── İmza yönetimi (görsel + yasal uyarı) ──
const SignatureSection = () => {
  const [sig, setSig] = useState(null);
  const [disclaimer, setDisclaimer] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/mail-signature")
    .then(({ data }) => { setSig(data); setDisclaimer(data.disclaimer_text || ""); })
    .catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_000_000) { toast.error("Görsel en fazla 1 MB olmalı"); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try { await api.post("/mail-signature/image", fd); toast.success("İmza görseli güncellendi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeImage = async () => {
    if (!window.confirm("İmza görseli kaldırılsın mı?")) return;
    try { await api.delete("/mail-signature/image"); toast.success("İmza görseli kaldırıldı"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveDisclaimer = async () => {
    setSaving(true);
    try { await api.put("/mail-signature", { disclaimer_text: disclaimer }); toast.success("Yasal uyarı metni kaydedildi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  if (!sig) return null;
  return (
    <div className="mt-6 pt-5 border-t border-[#E5E5E5]" data-testid="signature-section">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2 flex items-center gap-1.5">
        <ImageIcon className="w-3.5 h-3.5" /> Mail İmzası
      </div>
      <div className="text-[10px] text-neutral-400 mb-3">Mail gönderirken "imza ekle" seçiliyse bu görsel ve yasal uyarı metni mailin sonuna eklenir.</div>
      {sig.has_image ? (
        <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-3 inline-block">
          <img src={sig.image_data_uri} alt="Mevcut mail imzası" className="max-w-[380px] w-full h-auto" data-testid="signature-image-preview" />
        </div>
      ) : (
        <div className="border border-dashed border-[#E5E5E5] px-4 py-6 text-xs text-neutral-400 text-center">İmza görseli yüklenmemiş</div>
      )}
      <div className="flex items-center gap-2 mt-2">
        <label className="flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold bg-[#0A0A0A] text-white hover:bg-[#0020C2] cursor-pointer">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {sig.has_image ? "Görseli Değiştir" : "Görsel Yükle"}
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" data-testid="signature-upload-input" />
        </label>
        {sig.has_image && (
          <button onClick={removeImage} className="flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold border border-[#E5E5E5] hover:bg-[#DC2626] hover:text-white" data-testid="signature-remove-btn">
            <Trash2 className="w-3.5 h-3.5" /> Kaldır
          </button>
        )}
        <span className="text-[10px] text-neutral-400">JPG / PNG / WEBP · en fazla 1 MB · önerilen genişlik ~640px</span>
      </div>
      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yasal Uyarı Metni (imzanın altına eklenir)</label>
        <textarea data-testid="signature-disclaimer" value={disclaimer} onChange={(e) => setDisclaimer(e.target.value)} rows={6} maxLength={4000}
          className="mt-1 w-full px-3 py-2 border border-[#E5E5E5] text-xs leading-relaxed" />
        <div className="flex justify-end mt-2">
          <button data-testid="signature-save" onClick={saveDisclaimer} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Uyarı Metnini Kaydet
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Taslak yönetimi penceresi ──
export const MailTemplatesModal = ({ onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/email-templates").then(({ data }) => setTemplates(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) { toast.error("Ad, konu ve içerik gerekli"); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/email-templates/${form.id}`, { name: form.name, subject: form.subject, body: form.body });
      else await api.post("/email-templates", { name: form.name, subject: form.subject, body: form.body });
      toast.success(form.id ? "Taslak güncellendi" : "Taslak eklendi");
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`"${t.name}" taslağı silinsin mi?`)) return;
    try { await api.delete(`/email-templates/${t.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[600] flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl mt-10 border border-[#E5E5E5]" onClick={(e) => e.stopPropagation()} data-testid="mail-templates-modal">
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div className="font-bold font-heading flex items-center gap-2"><Mail className="w-4 h-4 text-[#0020C2]" /> Mail Taslakları</div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="templates-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">
          {!form && (
            <>
              <div className="space-y-2">
                {templates.map(t => (
                  <div key={t.id} className="border border-[#E5E5E5] px-3 py-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm">{t.name}</div>
                      <div className="text-xs text-neutral-500 truncate">{t.subject}</div>
                    </div>
                    <button data-testid={`tpl-edit-${t.id}`} onClick={() => setForm({ ...t })} className="p-1.5 hover:bg-[#F5F5F5]"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`tpl-del-${t.id}`} onClick={() => del(t)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <button data-testid="tpl-add" onClick={() => setForm({ name: "", subject: "", body: "{hitap}\n\n" })}
                className="mt-3 flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">
                <Plus className="w-3.5 h-3.5" /> Yeni Taslak
              </button>
              <SignatureSection />
            </>
          )}
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Taslak Adı</label>
                  <input data-testid="tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-[#E5E5E5] text-sm" placeholder="örn: Fiyat Teklifi Maili" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Konu</label>
                  <input data-testid="tpl-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 w-full px-3 py-2 border border-[#E5E5E5] text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">İçerik</label>
                <textarea data-testid="tpl-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={10} className="mt-1 w-full px-3 py-2 border border-[#E5E5E5] text-sm font-mono" />
                <div className="text-[10px] text-neutral-400 mt-1">Yer tutucular: <b>{"{hitap}"}</b> = "SEVGİ Hanım, Merhabalar" · <b>{"{firma}"}</b> = firma adı · <b>{"{yetkili}"}</b> = yetkili adı</div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setForm(null)} className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="tpl-save" onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Kaydet
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Firma detayında gönderilen mailler + geri dönüş takibi ──
export const SentEmailsSection = ({ firm, onChanged }) => {
  const mails = firm.sent_emails || [];
  const [noteFor, setNoteFor] = useState(null); // mail id
  const [note, setNote] = useState("");

  const setOutcome = async (mailId, outcome, outcomeNote) => {
    try {
      await api.put(`/tender-tracking/${firm.id}/emails/${mailId}/outcome`, { outcome, outcome_note: outcomeNote || "" });
      toast.success("Geri dönüş durumu güncellendi");
      setNoteFor(null); setNote("");
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (mails.length === 0) return null;
  return (
    <div className="mt-4" data-testid="sent-emails-section">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2 flex items-center gap-1.5">
        <Send className="w-3 h-3" /> Gönderilen Mailler ({mails.length})
      </div>
      <div className="space-y-1.5">
        {[...mails].reverse().map((m) => {
          const [, label, cls] = outcomeMeta(m.outcome);
          return (
            <div key={m.id} className="border border-[#E5E5E5] px-3 py-2 text-xs" data-testid={`sent-mail-${m.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="num font-bold">{fmtDT(m.sent_at)}</span>
                <span className="flex-1 truncate font-semibold">{m.subject}</span>
                {m.opened_at && (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-[#16A34A]/10 text-[#16A34A]" data-testid={`mail-opened-${m.id}`} title={`İlk açılma: ${fmtDT(m.opened_at)}${m.open_count > 1 ? ` · ${m.open_count} kez` : ""}`}>
                    <Eye className="w-3 h-3" /> Açıldı
                  </span>
                )}
                <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${cls}`} data-testid={`mail-outcome-${m.id}`}>{label}</span>
              </div>
              <div className="text-neutral-500 mt-0.5">{m.greeting} · {m.template_name} · {m.to}{m.with_signature ? " · imzalı" : ""}{m.sent_by ? ` · gönderen: ${m.sent_by}` : ""}</div>
              {m.outcome_note && <div className="text-neutral-600 mt-0.5 italic">"{m.outcome_note}"</div>}
              <div className="flex items-center gap-1 mt-1.5">
                <span className="text-[9px] uppercase tracking-widest text-neutral-400 mr-1">Geri dönüş:</span>
                <button data-testid={`mail-replied-${m.id}`} onClick={() => setOutcome(m.id, "replied", m.outcome_note)} className={`flex items-center gap-1 px-2 py-1 text-[9px] font-bold uppercase border ${m.outcome === "replied" ? "bg-[#0020C2] text-white border-[#0020C2]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}><Reply className="w-3 h-3" /> Yanıt Geldi</button>
                <button data-testid={`mail-positive-${m.id}`} onClick={() => setOutcome(m.id, "positive", m.outcome_note)} className={`flex items-center gap-1 px-2 py-1 text-[9px] font-bold uppercase border ${m.outcome === "positive" ? "bg-[#16A34A] text-white border-[#16A34A]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}><ThumbsUp className="w-3 h-3" /> Olumlu</button>
                <button data-testid={`mail-negative-${m.id}`} onClick={() => setOutcome(m.id, "negative", m.outcome_note)} className={`flex items-center gap-1 px-2 py-1 text-[9px] font-bold uppercase border ${m.outcome === "negative" ? "bg-[#DC2626] text-white border-[#DC2626]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}><ThumbsDown className="w-3 h-3" /> Olumsuz</button>
                <button data-testid={`mail-note-${m.id}`} onClick={() => { setNoteFor(noteFor === m.id ? null : m.id); setNote(m.outcome_note || ""); }} className="px-2 py-1 text-[9px] font-bold uppercase border border-[#E5E5E5] hover:bg-[#F5F5F5]">Not</button>
              </div>
              {noteFor === m.id && (
                <div className="flex gap-1.5 mt-1.5">
                  <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Geri dönüş notu..." className="flex-1 px-2 py-1 border border-[#E5E5E5] text-xs" data-testid={`mail-note-input-${m.id}`} />
                  <button onClick={() => setOutcome(m.id, m.outcome, note)} className="px-2.5 py-1 text-[9px] font-bold uppercase bg-[#0A0A0A] text-white" data-testid={`mail-note-save-${m.id}`}>Kaydet</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
